/* eslint-disable @typescript-eslint/no-explicit-any */
import type { CSSVariablesResolver } from '@mantine/core';
import { Card, Container, createTheme, Paper, rem, Select } from '@mantine/core';

const CONTAINER_SIZES = {
	xxs: rem('200px'),
	xs: rem('300px'),
	sm: rem('400px'),
	md: rem('500px'),
	lg: rem('600px'),
	xl: rem('1400px'),
	xxl: rem('1600px'),
};

const isKeyOf = <T extends object>(key: string | number | symbol, obj: T): key is keyof T => {
	return key in obj;
};

export const councilTheme = createTheme({
	fontFamily: 'Goldplay',
	primaryColor: 'blue',
	fontSizes: {
		xs: rem('12px'),
		sm: rem('14px'),
		md: rem('16px'),
		lg: rem('18px'),
		xl: rem('20px'),
		'2xl': rem('24px'),
		'3xl': rem('30px'),
		'4xl': rem('36px'),
		'5xl': rem('48px'),
	},
	spacing: {
		'3xs': rem('4px'),
		'2xs': rem('8px'),
		xs: rem('10px'),
		sm: rem('12px'),
		md: rem('16px'),
		lg: rem('20px'),
		xl: rem('24px'),
		'2xl': rem('28px'),
		'3xl': rem('32px'),
	},
	components: {
		Container: Container.extend({
			vars: (_, { size, fluid }) => ({
				root: {
					'--container-size': fluid
						? '100%'
						: size !== undefined && isKeyOf(size, CONTAINER_SIZES)
							? CONTAINER_SIZES[size]
							: rem(size),
				},
			}),
		}),
		Paper: Paper.extend({
			defaultProps: {
				p: 'md',
				shadow: 'xl',
				radius: 'md',
				withBorder: true,
			},
		}),

		Card: Card.extend({
			defaultProps: {
				p: 'xl',
				shadow: 'xl',
				radius: 'var(--mantine-radius-default)',
				withBorder: true,
			},
		}),
		Select: Select.extend({
			defaultProps: {
				checkIconPosition: 'right',
			},
		}),
	},
	colors: {
		green: [
			'#e7feed',
			'#d5f7df',
			'#adedbf',
			'#75e093', // BRAND GREEN
			'#5cda7f',
			'#45d46c',
			'#36d262',
			'#26ba51',
			'#1ba546',
			'#018f39',
		],
		blue: [
			'#e1fdff',
			'#d1f5fb',
			'#a7e7f2',
			'#91e0ed', // BRAND BLUE
			'#56cee2',
			'#3ec7de',
			'#2cc3dc',
			'#15acc4',
			'#0099af',
			'#00859a',
		],
		orange: [
			'#fff2e0',
			'#ffe3cb',
			'#ffc799',
			'#ffa863',
			'#ff923d', // BRAND ORANGE
			'#ff7d18',
			'#ff7406',
			'#e46300',
			'#cc5600',
			'#b24800',
		],
		red: [
			'#ffe8e9',
			'#ffd1d1',
			'#fba0a0',
			'#f76d6d',
			'#f44141',
			'#f22625',
			'#f21616',
			'#d8070b',
			'#c10007',
			'#a90003',
		],
	},
	other: {
		style: 'mantine',
	},
});

export const mantineCssVariableResolver: CSSVariablesResolver = () => ({
	variables: {
		curbyGreen: '#75e093',
		curbyBlue: '#91e0ed',
		curbyOrange: '#ff923d',
		curbyStroke: '#C7E8E9',
		curbyNavy: '#1A2C48',
		curbyBg: '#EEF9FB',
		curbyGrey: '#8998AF',
	},
	light: {
		// variables for light color scheme only
	},
	dark: {
		// variables for dark color scheme only
	},
});
